import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { User } from '../../types/User'
import { Tribe } from '../../types/Tribe'
import { useNavigate } from 'react-router'
import { useLazyGetAllUsersQuery } from '../../redux/user.endpoints'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faPlus,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons'
import { useLazyGetAllRolesQuery } from '../../redux/role.endpoints'
import { Role } from '../../types/Roles'
import { useLazyGetAllTribesQuery } from '../../redux/tribe.endpoints'
import { useLazyGetAllTeamsQuery } from '../../redux/team.endpoints'
import { SelectOption } from '../../types/SelectOption'
import { FlagKey } from '../../components/MemberFlags/memberFlags.constants'
import { FlagsFilterDropdown } from '../../components/MemberFlags/FlagsFilterDropdown'
import MembersTable, {
  getStatus,
} from '../../components/MembersTable/MembersTable'
import Loader from '../../components/Loader/Loader'

interface Props {
  companyId: number
}

const ALL = ''

const EmployeesView = ({ companyId }: Props) => {
  const navigate = useNavigate()
  const [employees, setEmployees] = useState([] as User[])
  const [searchQuery, setSearchQuery] = useState('')
  const [getAllRoles, getAllRolesResponse] = useLazyGetAllRolesQuery()
  const [tribes, setTribes] = useState([] as Tribe[])
  const [getTribes, { data: tribesData }] = useLazyGetAllTribesQuery()
  const [availableTeams, setAvailableTeams] = useState<SelectOption[]>([])
  const [getAllTeams, { data: teamsData }] = useLazyGetAllTeamsQuery()
  const [getUsers, { data, isLoading }] = useLazyGetAllUsersQuery()
  const [guilds, setGuilds] = useState<Array<Role>>([])
  const [selectedTeam, setSelectedTeam] = useState(ALL)
  const [selectedStatus, setSelectedStatus] = useState(ALL)
  const [selectedGuild, setSelectedGuild] = useState(ALL)
  const [selectedTribe, setSelectedTribe] = useState(ALL)
  const [selectedFlags, setSelectedFlags] = useState<FlagKey[]>([])

  useEffect(() => {
    setTribes(tribesData || [])
  }, [tribesData])

  useEffect(() => {
    if (teamsData) {
      const teamOptions = teamsData.map(team => ({
        value: team.id.toString(),
        label: team.name,
      }))
      setAvailableTeams(teamOptions)
    }
  }, [teamsData])

  useEffect(() => {
    getAllTeams(companyId)
  }, [getAllTeams, companyId])

  useEffect(() => {
    setEmployees(data || [])
  }, [data])

  useEffect(() => {
    getAllRoles()
  }, [])

  useEffect(() => {
    getUsers({
      companyId,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
      isActive: false,
    })
  }, [])

  useEffect(() => {
    if (getAllRolesResponse && getAllRolesResponse.data) {
      setGuilds(getAllRolesResponse.data)
    }
  }, [getAllRolesResponse])

  useEffect(() => {
    getTribes({
      companyId,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
    })
  }, [])

  const visibleEmployees = useMemo(
    () =>
      employees
        .filter(employee => {
          const fullName =
            `${employee.firstName} ${employee.lastName}`.toLowerCase()
          return fullName.includes(searchQuery.toLowerCase())
        })
        .filter(employee => {
          if (selectedGuild) {
            return employee.guildId.toString() === selectedGuild
          }
          return true
        })
        .filter(employee => {
          if (selectedTribe) {
            return employee.tribes.some(
              tribe => tribe.id.toString() === selectedTribe,
            )
          }
          return true
        })
        .filter(employee => {
          if (selectedTeam) {
            return employee.teamAllocations?.some(
              allocation => allocation.team?.id.toString() === selectedTeam,
            )
          }
          return true
        })
        .filter(employee => {
          if (selectedStatus === 'active') {
            return getStatus(employee.end_on) === 'Active'
          } else if (selectedStatus === 'inactive') {
            return getStatus(employee.end_on) === 'Inactive'
          }
          return true
        })
        .filter(employee => selectedFlags.every(key => Boolean(employee[key]))),
    [
      employees,
      searchQuery,
      selectedGuild,
      selectedTribe,
      selectedTeam,
      selectedStatus,
      selectedFlags,
    ],
  )

  return (
    <div className="page-content team-members-page">
      <Helmet title="Time Off - Members" />
      <div className="team-members-page__header">
        <h1 className="team-members-page__title">Manage Members</h1>
        <button
          className="tm-add-button"
          onClick={() => navigate('/members/new')}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add New Member
        </button>
      </div>
      <div className="team-members-page__filters">
        <div className="team-members-page__search">
          <input
            type="text"
            className="team-members-page__search-input"
            placeholder="Search by Name"
            aria-label="Search by Name"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <FontAwesomeIcon
            className="team-members-page__search-icon"
            icon={faSearch}
          />
        </div>
        <div className="team-members-page__filter-row">
          <div className="tm-filter">
            <span className="tm-filter__label">Guild</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedGuild}
                onChange={e => setSelectedGuild(e.target.value)}
              >
                <option value={ALL}>All Guilds</option>
                {guilds.map(guild => (
                  <option key={guild.id} value={guild.id.toString()}>
                    {guild.role}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
          <div className="tm-filter">
            <span className="tm-filter__label">Tribe</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedTribe}
                onChange={e => setSelectedTribe(e.target.value)}
              >
                <option value={ALL}>All Tribes</option>
                {tribes.map(tribe => (
                  <option key={tribe.id} value={tribe.id.toString()}>
                    {tribe.name}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
          <div className="tm-filter">
            <span className="tm-filter__label">Team</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedTeam}
                onChange={e => setSelectedTeam(e.target.value)}
              >
                <option value={ALL}>All Teams</option>
                {availableTeams.map(team => (
                  <option key={team.value} value={team.value}>
                    {team.label}
                  </option>
                ))}
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
          <div className="tm-filter">
            <span className="tm-filter__label">Status</span>
            <div className="tm-filter__select-wrap">
              <select
                className="tm-filter__select"
                value={selectedStatus}
                onChange={e => setSelectedStatus(e.target.value)}
              >
                <option value={ALL}>All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <FontAwesomeIcon
                className="tm-filter__chevron"
                icon={faChevronDown}
              />
            </div>
          </div>
          <FlagsFilterDropdown
            selected={selectedFlags}
            onChange={setSelectedFlags}
          />
        </div>
      </div>
      {isLoading ? <Loader /> : (<MembersTable
        employees={visibleEmployees}
        guilds={guilds}
        onEdit={employee => navigate(`/members/${employee.id}`)}
      />)}
    </div>
  )
}

export default EmployeesView
